import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Skeleton } from '@/components/ui/skeleton';
import { ProIncludedPill } from '@/components/ui/pro-pill';
import { MetaBlock } from '@/components/channels/MetaBlock';
import { ChannelPotentialBoost } from '@/components/channels/PotentialGauge';
import { ChannelIcon } from '@/components/channels/ChannelIcon';
import { LaunchStorefrontModal, markStorefrontLaunched } from '@/components/lifecycle/LaunchStorefrontModal';
import { NotFound } from '@/pages/errors/NotFound';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { formatCurrency } from '@/lib/currency';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useChannelActions } from '@/hooks/useChannelActions';
import { useStorefrontLaunchReadiness } from '@/hooks/useStorefrontLaunchReadiness';
import { awaitingUploadCopy } from '@/lib/storefront-launch';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useGoBack } from '@/hooks/useGoBack';
import { useVerifyEmail } from '@/components/verification/VerifyEmailProvider';
import { useTier } from '@/lib/tier';
import { buildChannel, getTitle, parseChannelSections, pricePillKind, IN_SCOPE_CHANNEL_IDS } from '@/lib/channels';

function DetailLoading() {
	return (
		<div className="space-y-8">
			<Skeleton className="h-4 w-32" />
			<Skeleton className="h-10 w-72" />
			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
				<Skeleton className="h-72" />
				<Skeleton className="h-72" />
			</div>
		</div>
	);
}

/** Primary CTA shown for a channel on its detail page. */
function ChannelCta({ channel, shopId, freeWithPro, claimed, onLaunchStorefront, launchReadiness }) {
	const navigate = useNavigate();
	const { shopPath } = useShopPath();
	const { enable, registerInterest, isBusy } = useChannelActions(shopId);
	const busy = isBusy(channel.id);
	// Policy-driven: a plan-granted website tool carries a list price but bills
	// nothing on this plan, so its CTA must not advertise "+ £10/mo".
	const isPaid = pricePillKind(channel) === 'price';
	const base = 'w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition';
	// A paid channel covered by a free Pro slot gets the brand gradient.
	const actionStyle =
		freeWithPro && !claimed
			? 'bg-gradient-to-r from-primary to-action text-white hover:opacity-90'
			: 'bg-action text-action-foreground hover:opacity-90';

	if (channel.live) {
		return (
			<Link to={shopPath(`/channels/${channel.id}/manage`)} className={`${base} bg-foreground text-background hover:opacity-90`}>
				Manage connection <ArrowRight className="h-3.5 w-3.5" />
			</Link>
		);
	}
	// Auto-provisioned channel mid-setup: there's nothing for the retailer to
	// enable — falling through to the Enable button would create a channel row
	// with no real connection behind it (NEA-6120).
	if (channel.settingUp) {
		return (
			<Link to={shopPath(`/channels/${channel.id}/manage`)} className={`${base} bg-action-soft text-action-soft-foreground`}>
				Being set up · view progress <ArrowRight className="h-3.5 w-3.5" />
			</Link>
		);
	}
	if (channel.enablement === 'ads-budget') {
		return (
			<Link to={shopPath('/ads?activate=1')} className={`${base} bg-action text-action-foreground hover:opacity-90`}>
				Set ad budget <ArrowRight className="h-3.5 w-3.5" />
			</Link>
		);
	}
	if (channel.enablement === 'register-interest') {
		if (channel.pending) {
			return <div className={`${base} cursor-default bg-action-soft text-action-soft-foreground`}>Interest registered</div>;
		}
		// Match the Lovable design — register-interest CTAs use the brand
		// gradient (the design's "Enable for free" treatment for these
		// channels) rather than the flat `bg-action` orange.
		return (
			<button
				type="button"
				disabled={busy}
				onClick={() => registerInterest(channel.id).catch(() => {})}
				className={`${base} bg-gradient-to-r from-primary to-action text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70`}
			>
				{busy ? 'Registering…' : 'Register interest'}
			</button>
		);
	}
	if (channel.enablement === 'launch-storefront') {
		// Enabling the Local Storefront goes through the LaunchStorefrontModal
		// celebration (loader + searchable-product count) — the modal fires the
		// actual enable via `onActivated`, same as the Channels empty state and
		// the Overview "unlock" card.
		//
		// Gated on the first stock upload having landed (NEA-6534): until then
		// the button is inert and says why, mirroring the Overview card.
		if (launchReadiness && !launchReadiness.canLaunch) {
			return (
				<div className="space-y-2">
					<button type="button" disabled aria-disabled="true" className={`${base} ${actionStyle} cursor-not-allowed opacity-60`}>
						Enable
					</button>
					<p className="text-xs text-muted-foreground">
						{launchReadiness.state === 'loading'
							? 'Checking your inventory…'
							: launchReadiness.state === 'products-arriving'
								? 'First stock upload received — unlocks as soon as your products are indexed.'
								: launchReadiness.state === 'no-source'
									? 'Connect your POS first — the storefront unlocks after your first stock upload.'
									: awaitingUploadCopy(launchReadiness)}
					</p>
				</div>
			);
		}
		return (
			<button
				type="button"
				disabled={busy}
				onClick={onLaunchStorefront}
				className={`${base} ${actionStyle} disabled:cursor-not-allowed disabled:opacity-70`}
			>
				{busy ? 'Enabling…' : 'Enable'}
			</button>
		);
	}
	if (channel.enablement === 'wizard') {
		const wizardLabel = channel.pending ? 'Continue setup' : freeWithPro && !claimed ? 'Set up for free' : 'Set up';
		// Wizard CTAs always wear the brand gradient — it signals that the
		// next click drops the retailer into a multi-step guided flow, not an
		// instant enable.
		return (
			<Link
				to={shopPath(`/channels/${channel.id}/setup`)}
				className={`${base} bg-gradient-to-r from-primary to-action text-white hover:opacity-90`}
			>
				<span>{wizardLabel}</span>
				{isPaid && !freeWithPro && !channel.pending ? (
					<span className="inline-flex items-baseline gap-0.5 text-xs font-semibold opacity-80">
						· +{formatCurrency(channel.price, channel.priceCurrency)}
						<span className="font-medium opacity-80">/mo</span>
					</span>
				) : null}
				<ArrowRight className="h-3.5 w-3.5" />
			</Link>
		);
	}
	const label = freeWithPro && !claimed ? 'Enable for free' : 'Enable';
	return (
		<button
			type="button"
			disabled={busy}
			onClick={() => {
				// `enable` owns the billing write (it POSTs the billing enable
				// endpoint, which assigns the _included price while a Pro
				// allowance slot is free) — no separate claimSlot, which
				// double-POSTed and raced this call.
				enable(channel.id)
					.then(() => navigate(shopPath(`/channels/${channel.id}/manage`)))
					.catch(() => {});
			}}
			className={`${base} ${actionStyle} disabled:cursor-not-allowed disabled:opacity-70`}
		>
			<span>{busy ? 'Enabling…' : label}</span>
			{isPaid && !freeWithPro ? (
				<span className="inline-flex items-baseline gap-0.5 text-xs font-semibold opacity-80">
					· +{formatCurrency(channel.price, channel.priceCurrency)}
					<span className="font-medium opacity-80">/mo</span>
				</span>
			) : null}
		</button>
	);
}

/** "Price" sidebar block — Local Ads, Pro-included, paid, or free. */
function PriceBlock({ channel, freeWithPro }) {
	if (channel.enablement === 'ads-budget') {
		return (
			<div className="space-y-1.5">
				<span className="inline-flex items-baseline gap-1 text-base font-bold text-foreground">
					From {formatCurrency(2, channel.priceCurrency)}
					<span className="text-xs font-medium text-muted-foreground">/day</span>
				</span>
				<p className="text-xs leading-snug text-muted-foreground">Paid straight to Google. Unify holds a 15% ad management fee.</p>
			</div>
		);
	}
	if (freeWithPro) {
		return (
			<div className="space-y-1.5">
				<ProIncludedPill />
				<p className="text-xs leading-snug text-muted-foreground">
					Worth {formatCurrency(channel.price, channel.priceCurrency)}/month — covered by one of your 2 free Pro channels.
				</p>
			</div>
		);
	}
	// Only a channel that actually bills shows its price — a plan-granted tool
	// carries a list price but reads "Included with Unify" on this plan.
	if (pricePillKind(channel) === 'price') {
		return (
			<span className="inline-flex items-baseline gap-1 text-base font-bold text-foreground">
				{formatCurrency(channel.price, channel.priceCurrency)}
				<span className="text-xs font-medium text-muted-foreground">/month</span>
			</span>
		);
	}
	return (
		<span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-1 text-sm font-semibold text-success-soft-foreground">
			Included with Unify
		</span>
	);
}

export function ChannelDetail() {
	const { id = '' } = useParams();
	const { shop } = useShop();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();
	const { billing } = useBillingStatus(shop?.id);
	const { isIncluded, slotsAvailable } = useTier();
	const { shopPath } = useShopPath();
	const goBack = useGoBack('/channels');
	const { enable } = useChannelActions(shop?.id);
	const { requireVerifiedEmail } = useVerifyEmail();
	const [launchOpen, setLaunchOpen] = useState(false);
	// Only the storefront route needs launch readiness, and only while the
	// storefront isn't live yet (a live shop has nothing to unlock).
	const storefrontLive =
		Array.isArray(channels) && channels.some((c) => c.type === 'storefront' && (c.enabled === true || c.status === 'enabled'));
	const launchReadiness = useStorefrontLaunchReadiness(id === 'storefront' && !storefrontLive ? shop?.id : null);
	// A broken cover image (e.g. the storefront's 404'd definition asset) must
	// fall back to the channel gradient, not the browser's broken-image glyph —
	// mirrors the same guard on ChannelCard (NEA-5911). Track the failed URL
	// rather than a boolean: this is the single route component for
	// /channels/:id, so boolean state would persist across channel navigation
	// and suppress the next channel's healthy image.
	const [failedCoverSrc, setFailedCoverSrc] = useState(null);

	if (!IN_SCOPE_CHANNEL_IDS.includes(id)) return <NotFound />;
	if (!shop?.id || channelsLoading || defsLoading) return <DetailLoading />;

	const channel = buildChannel(id, definitions, channels, billing?.entitlements, shop?.currency);
	if (!channel) return <NotFound />;

	const sections = parseChannelSections(channel.longDescription);
	const claimed = isIncluded(channel.id);
	// "Free with Pro" is a property of the channel's billing policy
	// (allowance-eligible, with a slot held or free) — never of merely having a
	// price while on Pro, which mis-labelled plan-granted website tools.
	const freeWithPro = pricePillKind(channel, { claimed, slotsAvailable }) === 'free-with-pro';
	const worksWith = (channel.worksWith ?? [])
		.map((worksWithId) => ({ id: worksWithId, definition: definitions[worksWithId] }))
		.filter((entry) => entry.definition);

	return (
		<div className="space-y-8">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex cursor-pointer items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-3 w-3" /> Back
			</button>

			<header className="flex items-start gap-4">
				<ChannelIcon channelId={channel.id} src={channel.iconUrl} className="h-12 w-12" />
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="text-2xl font-extrabold tracking-tight md:text-3xl">{channel.title}</h1>
						{channel.tag ? (
							<span className="rounded-full bg-action-soft px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-action-soft-foreground">
								{channel.tag}
							</span>
						) : null}
					</div>
					<p className="mt-1.5 max-w-3xl text-base text-muted-foreground">{channel.description}</p>
				</div>
			</header>

			<div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
				<div className="min-w-0 space-y-8">
					<div className={`h-64 overflow-hidden rounded-2xl md:h-72 ${channel.heroGradient}`}>
						{channel.coverImageUrl && failedCoverSrc !== channel.coverImageUrl ? (
							<img
								src={channel.coverImageUrl}
								alt=""
								onError={() => setFailedCoverSrc(channel.coverImageUrl)}
								className="h-full w-full object-cover"
							/>
						) : null}
					</div>

					{sections.length > 0 ? (
						sections.map((section) => (
							<section key={section.heading}>
								<h2 className="mb-3 text-xl font-bold text-foreground">{section.heading}</h2>
								{section.list ? (
									<ul className="list-disc space-y-1.5 pl-5 text-[15px] leading-relaxed text-foreground/90">
										{section.list.map((item) => (
											<li key={item}>{item}</li>
										))}
									</ul>
								) : (
									<div className="space-y-3 text-[15px] leading-relaxed text-foreground/90">
										{section.paragraphs.map((paragraph) => (
											<p key={paragraph}>{paragraph}</p>
										))}
									</div>
								)}
							</section>
						))
					) : (
						<SoftCard className="!p-8 text-center">
							<p className="text-sm text-muted-foreground">Detailed content coming soon for this channel.</p>
						</SoftCard>
					)}
				</div>

				<aside className="space-y-5">
					<ChannelCta
						channel={channel}
						shopId={shop.id}
						freeWithPro={freeWithPro}
						claimed={claimed}
						onLaunchStorefront={() => requireVerifiedEmail(() => setLaunchOpen(true))}
						launchReadiness={channel.enablement === 'launch-storefront' ? launchReadiness : null}
					/>

					<div className="space-y-5 text-sm">
						{channel.category ? (
							<MetaBlock label="Category">
								<span className="inline-flex rounded-full bg-muted px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
									{channel.category}
								</span>
							</MetaBlock>
						) : null}

						<MetaBlock label={channel.enablement === 'ads-budget' ? 'Ad budget' : 'Price'}>
							<PriceBlock channel={channel} freeWithPro={freeWithPro} />
						</MetaBlock>

						<ChannelPotentialBoost channelId={channel.id} />

						{worksWith.length > 0 ? (
							<MetaBlock label="Works with">
								<ul className="space-y-1.5">
									{worksWith.map((entry) => (
										<li key={entry.id}>
											{IN_SCOPE_CHANNEL_IDS.includes(entry.id) ? (
												<Link to={shopPath(`/channels/${entry.id}`)} className="text-action hover:underline">
													{getTitle(entry.id, entry.definition)}
												</Link>
											) : (
												<span className="text-foreground">{getTitle(entry.id, entry.definition)}</span>
											)}
										</li>
									))}
								</ul>
							</MetaBlock>
						) : null}

						{Array.isArray(channel.helpArticleUrls) && channel.helpArticleUrls.length > 0 ? (
							<MetaBlock label="Support">
								<ul className="space-y-1.5">
									{channel.helpArticleUrls.map((article) => (
										<li key={article.url}>
											<a href={article.url} target="_blank" rel="noreferrer" className="text-action hover:underline">
												{article.title}
											</a>
										</li>
									))}
								</ul>
							</MetaBlock>
						) : null}
					</div>
				</aside>
			</div>

			{/* Mounted at page level so the modal survives the channel flipping
			    live mid-celebration (the enable in `onActivated` re-renders the
			    CTA into "Manage connection"). */}
			<LaunchStorefrontModal
				open={launchOpen}
				onClose={() => setLaunchOpen(false)}
				onActivated={() => {
					enable('storefront').catch(() => {});
					markStorefrontLaunched();
				}}
			/>
		</div>
	);
}
